import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, CalendarClock, GitBranch } from 'lucide-react';
import { apiClient } from '../api/client';
import { useBoardUpdates } from '../hooks/useBoardUpdates';
import { playNotificationSound } from '../utils/sound';
import { showBrowserNotification } from '../utils/browserNotify';
import type { OrderRequest } from '../types';

function formatWhen(iso: string | null): string {
  if (!iso) return 'без времени';
  return new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}

function headline(r: OrderRequest): string {
  const who = r.requestedBy?.fullName ?? r.order.technician?.fullName ?? 'Сантехник';
  if (r.type === 'RESCHEDULE') return `${who} просит перенести наряд №${r.order.orderNumber}`;
  if (r.type === 'CANCEL') return `${who} просит отменить наряд №${r.order.orderNumber}`;
  return `Сделку по наряду №${r.order.orderNumber} перевели на стадию «${r.bitrixStageName ?? r.bitrixStageId ?? '?'}»`;
}

/**
 * Наряд сам не закрывается и не переносится — решение владельца 24.09.2026.
 *
 * Сюда приходят три вида вопросов:
 * - сантехник просит перенести наряд;
 * - сантехник просит отменить наряд;
 * - сделку в Битриксе увели со стадии «Назначен мастер».
 *
 * Окно всплывает у всех, кто сейчас в панели. Ответивший первым решает, остальным окно гаснет
 * по сигналу board:update 'requests'. Кто откроет панель позже, увидит вопрос при входе
 */
export default function OrderRequestsPopup() {
  const [requests, setRequests] = useState<OrderRequest[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Свёрнутое окно — плашка в углу: чтобы посмотреть наряд или календарь перед решением.
  // Новый вопрос раскрывает окно снова
  const [collapsed, setCollapsed] = useState(false);
  const known = useRef<Set<string> | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<OrderRequest[]>('/order-requests');
      // Звук и системное уведомление — только на новые вопросы, а не на каждое обновление
      // списка и не на те, что уже висели при открытии панели
      if (known.current) {
        const fresh = data.filter((r) => !known.current!.has(r.id));
        if (fresh.length > 0) {
          setCollapsed(false);
          playNotificationSound();
          showBrowserNotification('Нужно ваше решение — Труба-Дело', headline(fresh[0]));
        }
      }
      known.current = new Set(data.map((r) => r.id));
      setRequests(data);
    } catch {
      // нет связи — попробуем при следующем сигнале или через минуту
    }
  }, []);

  useEffect(() => {
    void load();
    // На случай оборванного сокета: сигнал board:update мог не дойти
    const timer = setInterval(() => void load(), 60_000);
    return () => clearInterval(timer);
  }, [load]);
  useBoardUpdates(['requests'], () => void load());

  const current = requests[0];
  if (!current) return null;

  if (collapsed) {
    return (
      <button className="request-pill" onClick={() => setCollapsed(false)}>
        Ждут вашего решения: {requests.length}
      </button>
    );
  }

  const decide = async (action: 'approve' | 'deny') => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.patch(`/order-requests/${current.id}/${action}`);
    } catch (e) {
      const message = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(message ?? 'Не удалось — проверьте связь и попробуйте ещё раз');
    } finally {
      setBusy(false);
      await load();
    }
  };

  const fromBitrix = current.type === 'BITRIX_STAGE_LEFT';
  const Icon = current.type === 'RESCHEDULE' ? CalendarClock : current.type === 'CANCEL' ? Ban : GitBranch;
  const kind = current.type === 'RESCHEDULE' ? 'Перенос наряда' : current.type === 'CANCEL' ? 'Отмена наряда' : 'Сделка в Битриксе';

  return (
    <div className="modal-overlay request-overlay" role="dialog" aria-modal="true" aria-labelledby="request-title">
      <div className="modal-panel request-panel">
        <div className={`request-kind request-kind-${current.type.toLowerCase()}`}>
          <Icon size={15} strokeWidth={2.3} />
          {kind}
        </div>
        <h2 id="request-title" className="request-title">{headline(current)}</h2>

        <dl className="request-details">
          <dt>Адрес</dt>
          <dd>{current.order.address}</dd>
          <dt>Клиент</dt>
          <dd>{current.order.clientName}</dd>
          {fromBitrix && (
            <>
              <dt>Сантехник</dt>
              <dd>{current.order.technician?.fullName ?? 'не назначен'}</dd>
            </>
          )}
          {current.type === 'RESCHEDULE' ? (
            <>
              <dt>Сейчас</dt>
              <dd>{formatWhen(current.order.scheduledAt)}</dd>
              <dt>Просит на</dt>
              <dd className="request-emphasis">{formatWhen(current.proposedScheduledAt)}</dd>
            </>
          ) : current.type === 'CANCEL' ? (
            <>
              <dt>Причина</dt>
              <dd className="request-emphasis">{current.reason || 'не указана'}</dd>
            </>
          ) : null}
        </dl>

        {fromBitrix && (
          <p className="request-note">
            Раньше наряд от этого отменялся сам. Теперь он остаётся у сантехника, пока вы не решите.
          </p>
        )}

        {error && <div className="request-error">{error}</div>}

        <div className="request-actions">
          <div className="request-links">
            <Link className="request-open-link" to={`/orders/${current.orderId}`} onClick={() => setCollapsed(true)}>
              Открыть наряд
            </Link>
            <button className="btn-link request-collapse" onClick={() => setCollapsed(true)}>
              Свернуть
            </button>
          </div>
          <div className="request-buttons">
            <button className="btn-outline-danger" onClick={() => void decide('deny')} disabled={busy}>
              {fromBitrix ? 'Оставить в работе' : 'Запретить'}
            </button>
            <button onClick={() => void decide('approve')} disabled={busy}>
              {fromBitrix ? 'Снять наряд' : 'Одобрить'}
            </button>
          </div>
        </div>

        {requests.length > 1 && <div className="request-more">Ещё запросов: {requests.length - 1}</div>}
      </div>
    </div>
  );
}
